import React, { useEffect, useMemo, useState } from 'react';
import { TIMELINE, TimelineEvent } from '../content/timeline';
import { useLang, T } from '../lib/lang';

function kindStyles(kind: TimelineEvent['kind']) {
  if (kind === 'work') {
    return {
      dot: 'bg-gold shadow-[0_0_18px_rgba(212,163,115,0.35)]',
      border: 'border-gold',
      stem: 'from-gold/80 to-transparent',
    };
  }
  return {
    dot: 'bg-bg border border-blue',
    border: 'border-blue',
    stem: 'from-blue/60 to-transparent',
  };
}

export function Timeline() {
  const { lang } = useLang();
  const [active, setActive] = useState<TimelineEvent | null>(null);
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(max-width: 767px)').matches;
  });

  const activeStyles = useMemo(() => (active ? kindStyles(active.kind) : null), [active]);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const update = () => setIsMobile(mq.matches);
    update();
    if ('addEventListener' in mq) {
      mq.addEventListener('change', update);
      return () => mq.removeEventListener('change', update);
    }
    // Safari < 14
    // @ts-expect-error - legacy API
    mq.addListener(update);
    // @ts-expect-error - legacy API
    return () => mq.removeListener(update);
  }, []);

  return (
    <section id="timeline" className="border-b border-border py-20">
      <div className="mx-auto max-w-6xl px-6">
        <div className="mb-10 flex items-end justify-between gap-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-dim">
              <T ru="timeline" en="timeline" />
            </div>
            <h2 className="mt-3 font-serif text-3xl font-light italic text-text md:text-4xl">
              <T ru="путь к IFS + AI" en="path to IFS + AI" />
            </h2>
            <p className="mt-4 max-w-2xl text-sm leading-7 text-muted">
              <T
                ru="две оси: операционный опыт (работа) и внутренний путь. наведи на точку (или тапни на мобиле), чтобы раскрыть контекст."
                en="two axes: operational experience (work) and inner path. hover a point (or tap on mobile) to reveal context."
              />
            </p>
          </div>

          <div className="hidden items-center gap-6 md:flex">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted">
              <span className="h-2 w-2 rounded-full bg-gold" />
              <T ru="работа" en="work" />
            </div>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted">
              <span className="h-2 w-2 rounded-full border border-blue bg-bg" />
              <T ru="внутренний путь" en="inner path" />
            </div>
          </div>
        </div>
      </div>

      <div className="relative overflow-x-auto">
        <div className="mx-auto min-w-[980px] max-w-6xl px-6">
          <div className="relative py-20">
            <div className="absolute left-0 right-0 top-1/2 h-px bg-border" />

            <div className="flex">
              {TIMELINE.map((e, idx) => {
                const s = kindStyles(e.kind);
                const isTop = e.kind === 'inner';
                const pos = isTop ? 'top-10' : 'bottom-10';
                const stemPos = isTop ? 'top-[52px]' : 'bottom-[52px]';
                const contentPos = isTop ? 'top-[98px]' : 'bottom-[98px]';
                const align = isTop ? 'pt-16' : 'pb-16';

                return (
                  <button
                    key={`${e.year}-${idx}`}
                    className={`relative w-40 shrink-0 ${align} cursor-pointer text-center`}
                    onMouseEnter={() => {
                      if (isMobile) return;
                      setActive(e);
                    }}
                    onMouseLeave={() => {
                      if (isMobile) return;
                      setActive(null);
                    }}
                    onFocus={() => {
                      if (isMobile) return;
                      setActive(e);
                    }}
                    onBlur={() => {
                      if (isMobile) return;
                      setActive(null);
                    }}
                    onClick={() => {
                      if (!isMobile) return;
                      setActive((prev) => (prev === e ? null : e));
                    }}
                    type="button"
                    aria-label={`${e.year} · ${lang === 'ru' ? e.title.ru : e.title.en}`}
                  >
                    {/* Dot */}
                    <div
                      className={[
                        'absolute left-1/2 h-2 w-2 -translate-x-1/2 rounded-full transition-transform duration-200',
                        pos,
                        e.kind === 'work' ? s.dot : `border ${s.border} ${s.dot}`,
                        active === e ? 'scale-150' : 'scale-100',
                      ].join(' ')}
                    />

                    {/* Stem */}
                    <div
                      className={[
                        'absolute left-1/2 w-px -translate-x-1/2 bg-gradient-to-b',
                        stemPos,
                        isTop ? 'h-10' : 'h-10 rotate-180',
                        s.stem,
                        'opacity-70',
                      ].join(' ')}
                    />

                    {/* Label */}
                    <div className={`absolute left-1/2 -translate-x-1/2 ${contentPos} w-[150px]`}>
                      <div className="text-[11px] text-text">{e.year}</div>
                      <div className="mt-1 text-[10px] text-muted transition-colors group-hover:text-text">
                        {lang === 'ru' ? e.title.ru : e.title.en}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Desktop hover tooltip */}
      {!isMobile && (
        <div
          className={[
            'fixed left-1/2 top-1/2 z-[60] w-[360px] -translate-x-1/2 -translate-y-1/2 rounded border bg-bg/95 p-6 text-center backdrop-blur',
            active ? 'pointer-events-none opacity-100' : 'pointer-events-none opacity-0',
            'transition-opacity duration-200',
            activeStyles?.border ?? 'border-border',
          ].join(' ')}
          aria-hidden={!active}
        >
          {active && (
            <>
              <div className="text-2xl font-light text-text">{active.year}</div>
              <div className="mt-2 text-sm text-text">{lang === 'ru' ? active.title.ru : active.title.en}</div>
              <div className="mt-4 text-[12px] leading-6 text-muted">
                {lang === 'ru' ? active.desc.ru : active.desc.en}
              </div>
            </>
          )}
        </div>
      )}

      {/* Mobile details card (no overlay) */}
      {isMobile && active && (
        <div className="mx-auto mt-6 max-w-6xl px-6">
          <div className={['rounded-lg border bg-bg/60 p-5 backdrop-blur', activeStyles?.border ?? 'border-border'].join(' ')}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-[12px] text-dim">{active.year}</div>
                <div className="mt-1 text-sm text-text">{lang === 'ru' ? active.title.ru : active.title.en}</div>
              </div>
              <button
                type="button"
                onClick={() => setActive(null)}
                className="rounded border border-border px-3 py-1 text-[10px] uppercase tracking-widest text-muted hover:border-gold hover:text-gold"
              >
                <T ru="закрыть" en="close" />
              </button>
            </div>
            <div className="mt-4 text-[12px] leading-6 text-muted">{lang === 'ru' ? active.desc.ru : active.desc.en}</div>
          </div>
        </div>
      )}
    </section>
  );
}


